import { FronteggContext } from '@frontegg/rest-api';
import { Axios } from 'src/lib';
import {
  srColumnMappingsLoadingSlice,
  srColumnMappingsSlice,
  srTimezoneLoadingSlice,
  srTimezoneSlice,
  srProviderMappingLoadingSlice,
  srProviderMappingsSlice,
  srTypeListSlice,
  srTypeMappingsSlice,
  srTypeMappingsLoadingSlice,
  srBulkUploadHistorySlice,
  srBulkUploadHistoryLoadingSlice,
  srTypeListLoadingSlice,
  srStatusMappingsSlice,
  srStatusMappingsLoadingSlice,
  srStatusListSlice,
  srStatusListLoadingSlice,
} from '../slices/service_request.slice';
import {
  getOrganizationSettingsMappingConfig,
  getSRTypeListConfig,
  getSRStatusListConfig,
} from '../lib/service_request.url';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';

export const getSRColumnMappings = (
  org_uuid,
  key = 'service_request_bulk_upload_settings'
) => {
  return async (dispatch) => {
    try {
      dispatch(srColumnMappingsLoadingSlice(true));
      const { url } = getOrganizationSettingsMappingConfig(key);
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Impersonate-Org-UUID': `${org_uuid}`,
        'Headless-Mode': true,
      };
      const result = await Axios.get(url, { headers });
      const response = result?.data;
      const column_mapping = response?.settings?.column_mapping;
      if (result?.status === 200 && column_mapping) {
        dispatch(srColumnMappingsSlice(column_mapping));
      } else {
        dispatch(srColumnMappingsSlice({}));
      }
    } catch (e) {
      dispatch(srColumnMappingsSlice({}));
    } finally {
      dispatch(srColumnMappingsLoadingSlice(false));
    }
  };
};

export const getSRTimezone = (
  org_uuid,
  key = 'service_request_bulk_upload_settings'
) => {
  return async (dispatch) => {
    try {
      dispatch(srTimezoneLoadingSlice(true));
      const { url } = getOrganizationSettingsMappingConfig(key);
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Impersonate-Org-UUID': `${org_uuid}`,
        'Headless-Mode': true,
      };
      const result = await Axios.get(url, { headers });
      const response = result?.data;
      const timezone = response?.settings?.timezone;
      if (result?.status === 200 && timezone) {
        dispatch(srTimezoneSlice(timezone));
      } else {
        dispatch(srTimezoneSlice(null));
      }
    } catch (e) {
      dispatch(srTimezoneSlice(null));
    } finally {
      dispatch(srTimezoneLoadingSlice(false));
    }
  };
};

export const updateSRMappings = (
  payload,
  org_uuid,
  mapping_type,
  key = 'service_request_bulk_upload_settings'
) => {
  return async () => {
    try {
      const { url } = getOrganizationSettingsMappingConfig(key);
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Impersonate-Org-UUID': `${org_uuid}`,
        'Headless-Mode': true,
      };
      const result = await Axios.put(url, payload, { headers });
      const response = result?.data;
      if ((result?.status === 200 || result?.status === 201) && response) {
        showToastSuccess(
          `${mapping_type.charAt(0).toUpperCase() + mapping_type.slice(1)} updated successfully`
        );
      }
    } catch (e) {
      showToastError(`Error updating ${mapping_type}`);
    }
  };
};

export const getSRProviderMappings = (
  org_uuid,
  key = 'service_request_bulk_upload_settings'
) => {
  return async (dispatch) => {
    try {
      dispatch(srProviderMappingLoadingSlice(true));
      const { url } = getOrganizationSettingsMappingConfig(key);
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Impersonate-Org-UUID': `${org_uuid}`,
        'Headless-Mode': true,
      };
      const result = await Axios.get(url, { headers });
      const response = result?.data;
      const provider_mapping = response?.settings?.practitioner_mapping;
      if (result?.status === 200 && provider_mapping) {
        // save reverse mapping for easy lookup
        const reverse_mapping = {};
        for (const [key, value] of Object.entries(provider_mapping)) {
          if (!Object.prototype.hasOwnProperty.call(reverse_mapping, value)) {
            reverse_mapping[value] = [key];
          } else {
            reverse_mapping[value].push(key);
          }
        }
        dispatch(srProviderMappingsSlice(reverse_mapping));
      }
    } catch (e) {
      // console.error("Could not fetch provider mappings:", e);
      dispatch(srProviderMappingsSlice({}));
    } finally {
      dispatch(srProviderMappingLoadingSlice(false));
    }
  };
};

export const setBulkSRUploadSetting = (org_uuid, payload) => {
  return async () => {
    try {
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Impersonate-Org-UUID': `${org_uuid}`,
        'Headless-Mode': true,
      };
      const result = await Axios.put(`/organization_setting`, payload, {
        headers,
      });
      if (result?.status === 200) {
        showToastSuccess(
          `Service request bulk uploads ${payload?.settings?.enable_service_request_upload ? 'enabled' : 'disabled'}`
        );
      }
    } catch (e) {
      showToastError('Error occurred');
    }
  };
};

export const getSRTypeList = (org_uuid) => {
  return async (dispatch) => {
    try {
      dispatch(srTypeListLoadingSlice(true));
      const { url } = getSRTypeListConfig();
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Impersonate-Org-UUID': `${org_uuid}`,
        'Headless-Mode': true,
      };
      const result = await Axios.get(url, { headers });
      const response = result?.data;
      if (result?.status === 200 && response) {
        dispatch(srTypeListSlice(response?.data));
      }
    } catch (e) {
      console.error('Error fetching SR type list:', e);
      dispatch(srTypeListSlice([]));
    } finally {
      dispatch(srTypeListLoadingSlice(false));
    }
  };
};

export const getSRTypeMappings = (
  org_uuid,
  key = 'service_request_bulk_upload_settings'
) => {
  return async (dispatch) => {
    try {
      dispatch(srTypeMappingsLoadingSlice(true));
      const { url } = getOrganizationSettingsMappingConfig(key);
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Impersonate-Org-UUID': `${org_uuid}`,
        'Headless-Mode': true,
      };
      const result = await Axios.get(url, { headers });
      const response = result?.data;
      const sr_type_mapping = response?.settings?.service_request_type_mapping;
      if (result?.status === 200 && sr_type_mapping) {
        const reverse_mapping = {};
        for (const [key, value] of Object.entries(sr_type_mapping)) {
          if (!Object.prototype.hasOwnProperty.call(reverse_mapping, value)) {
            reverse_mapping[value] = [key];
          } else {
            reverse_mapping[value].push(key);
          }
        }
        dispatch(srTypeMappingsSlice(reverse_mapping));
      } else {
        dispatch(srTypeMappingsSlice({}));
      }
    } catch (e) {
      dispatch(srTypeMappingsSlice({}));
    } finally {
      dispatch(srTypeMappingsLoadingSlice(false));
    }
  };
};

export const getBulkServiceRequestUploadHistory = (page, per_page) => {
  return async (dispatch) => {
    const token = FronteggContext.getAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
    dispatch(srBulkUploadHistoryLoadingSlice(true));
    dispatch(srBulkUploadHistorySlice([]));
    try {
      const params = {
        page: page,
        per_page: per_page,
      };
      const result = await Axios.get('/v3/sync_service_requests/history', {
        headers,
        params,
      });
      if (result?.status === 200) {
        dispatch(srBulkUploadHistorySlice(result.data));
      } else {
        showToastError('Failed to get service request upload history');
      }
    } catch (error) {
      console.error(error);
      showToastError(
        `Error getting service request upload history: ${error.message}`
      );
    } finally {
      dispatch(srBulkUploadHistoryLoadingSlice(false));
    }
  };
};

export const syncServiceRequests = (data) => {
  return async () => {
    const token = FronteggContext.getAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };
    try {
      const result = await Axios.post('/v3/sync_service_requests', data, {
        headers,
      });
      if (result?.status === 200) {
        return result.data;
      } else {
        showToastError('Failed to upload service requests');
      }
    } catch (error) {
      console.error(error);
      showToastError(`Error uploading service requests: ${error.message}`);
    }
  };
};

export const getSRStatusList = () => {
  return async (dispatch) => {
    try {
      dispatch(srStatusListLoadingSlice(true));
      const { url } = getSRStatusListConfig();
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Headless-Mode': true,
      };
      const result = await Axios.get(url, { headers });
      const response = result?.data;
      if (result?.status === 200 && response) {
        dispatch(srStatusListSlice(response?.data));
      }
    } catch (e) {
      console.error('Error fetching SR status list:', e);
      dispatch(srStatusListSlice([]));
    } finally {
      dispatch(srStatusListLoadingSlice(false));
    }
  };
};

export const getSRStatusMappings = (
  org_uuid,
  key = 'service_request_bulk_upload_settings'
) => {
  return async (dispatch) => {
    try {
      dispatch(srStatusMappingsLoadingSlice(true));
      const { url } = getOrganizationSettingsMappingConfig(key);
      const token = FronteggContext.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'Impersonate-Org-UUID': `${org_uuid}`,
        'Headless-Mode': true,
      };
      const result = await Axios.get(url, { headers });
      const response = result?.data;
      const sr_status_mapping =
        response?.settings?.service_request_status_mapping;
      if (result?.status === 200 && sr_status_mapping) {
        dispatch(srStatusMappingsSlice(sr_status_mapping));
      } else {
        dispatch(srStatusMappingsSlice({}));
      }
    } catch (e) {
      dispatch(srStatusMappingsSlice({}));
    } finally {
      dispatch(srStatusMappingsLoadingSlice(false));
    }
  };
};
